import axios from 'axios';
import i18next from 'i18next';
import { get } from 'lodash-es';

import { services } from 'services/base';
import {
  AddHelpAppContextService,
  AddHelpContextTopicsService,
  GetHelpTopicsService,
  HelpAppContext,
  HelpTopic,
  HelpTopicWithKey,
  HelpTopics,
  OnHelpTopicsChangeService,
  OpenHelpTopicService,
  RemoveHelpAppContextService,
} from 'types';
import { HelpConfig } from 'types/configs/main';
import { getMainConfig, logger } from 'utils';

type HelpAppContextWithContextTopics = HelpAppContext & {
  contextTopics: string[][]; // [contextTopics][topics]
};

type HelpState = {
  appContexts: HelpAppContextWithContextTopics[];
  actualTopics: HelpTopics;
};

const initHelpServices = async () => {
  const { clearAction, executeAction, registerAction, registerService } = services;

  const state: HelpState = {
    appContexts: [],
    actualTopics: {
      defaultTopics: [],
      contextTopics: [],
    },
  };

  const getActualAppContext = () => {
    const { appContexts } = state;
    const count = appContexts.length;
    return count ? appContexts[count - 1] : null;
  };

  const getActualContextTopics = () => {
    const appContext = getActualAppContext();
    if (!appContext) {
      return null;
    }

    const { contextTopics } = appContext;
    const count = contextTopics?.length;
    return count ? contextTopics[count - 1] : null;
  };

  const mapTopics = (allTopics: Record<string, HelpTopic>, topics: string[] | null) => {
    const result: HelpTopicWithKey[] = [];

    topics?.forEach((topicKey) => {
      const topic = get(allTopics, topicKey, null);
      if (!topic) {
        logger.warn(`Help topics '${topicKey}' does not exists!`);
        return;
      }
      result.push({
        ...topic,
        key: topicKey,
      });
    });

    return result;
  };

  const calculateActualTopics = () => {
    const result: HelpTopics = {
      defaultTopics: [],
      contextTopics: [],
    };

    const appContext = getActualAppContext();
    if (appContext) {
      const { defaultTopics = null, allTopics } = appContext;
      result.defaultTopics = mapTopics(allTopics, defaultTopics);
      result.contextTopics = mapTopics(allTopics, getActualContextTopics());
    }

    return result;
  };

  const SERVICE_PREFIX = 'appFrame.help';
  const ACTION_SET_HELP_TOPICS = 'AppFrame:Help:SetTopics';

  registerService<GetHelpTopicsService>(`${SERVICE_PREFIX}.getTopics`, () => state.actualTopics);
  registerAction(
    ACTION_SET_HELP_TOPICS,
    (topics: HelpTopics) => {
      state.actualTopics = topics;
    },
    {
      order: 0,
    },
  );
  registerService<OnHelpTopicsChangeService>(`${SERVICE_PREFIX}.onTopicsChange`, (callback) => {
    registerAction(ACTION_SET_HELP_TOPICS, callback, {
      order: 1,
    });
    return () => clearAction(ACTION_SET_HELP_TOPICS, callback);
  });

  registerService<OpenHelpTopicService>(
    `${SERVICE_PREFIX}.openTopic`,
    (topicName: string, target?: string, features?: string) => {
      const appContext = getActualAppContext();
      const helpTopic = get(appContext?.allTopics, topicName, null);
      if (!helpTopic) {
        logger.warn(`Help topic ${helpTopic} does not exists!`);
        return null;
      }

      const url = appContext?.url.replace('{{lang}}', i18next.language).replace('{{file}}', helpTopic.file);
      const win = window.open(url, target || 'AppFrame:Help', features);

      return win;
    },
  );

  registerService<AddHelpAppContextService>(`${SERVICE_PREFIX}.addAppContext`, (context: HelpAppContext) => {
    state.appContexts.push({
      ...context,
      contextTopics: [],
    });
    executeAction(ACTION_SET_HELP_TOPICS, calculateActualTopics());
  });

  registerService<RemoveHelpAppContextService>(`${SERVICE_PREFIX}.removeAppContext`, () => {
    if (state.appContexts.pop()) {
      executeAction(ACTION_SET_HELP_TOPICS, calculateActualTopics());
    }
  });

  registerService<AddHelpContextTopicsService>(`${SERVICE_PREFIX}.addContextTopics`, (topics: string[]) => {
    const appContext = getActualAppContext();
    if (!appContext) {
      return;
    }

    appContext.contextTopics.push(topics);
    executeAction(ACTION_SET_HELP_TOPICS, calculateActualTopics());
  });

  registerService<AddHelpContextTopicsService>(`${SERVICE_PREFIX}.removeContextTopics`, () => {
    const appContext = getActualAppContext();

    if (appContext?.contextTopics.pop()) {
      executeAction(ACTION_SET_HELP_TOPICS, calculateActualTopics());
    }
  });

  const helpConfig: HelpConfig | null = getMainConfig('help') || null;
  if (helpConfig) {
    try {
      const response = await axios.get(helpConfig.allTopicsUrl);
      services.appFrame.help.addAppContext({
        ...helpConfig,
        allTopics: response.data,
      });
    } catch (err) {
      logger.error('Error loading help configuration!', err);
    }
  }
};

export default initHelpServices;
